/**
 * ProductsServicesStable — agora wrapeia o cliente V2 (productsV2API)
 * e traduz o shape pra o que a UI legada espera (image, price em centavos
 * no objeto plano em vez de price_cents, etc.) via v2ShapeMapper.
 *
 * Mantém a mesma assinatura pública dos métodos pra não quebrar callers
 * (ProductContext, registros de produto, etc).
 */

import { productsV2API } from './v2/products';
import { plansV2API } from './v2/plans';
import {
  mapProductV2ToV1,
  mapProductListV2ToV1,
} from '../../utils/v2ShapeMapper';

/**
 * Carrega planos de um produto e devolve no shape V1 (price em vez de price_cents).
 */
async function attachPlansV1Shape(product) {
  if (!product?.id) return product;
  try {
    const plansRes = await plansV2API.listByProduct(product.id);
    const plans = plansRes?.data?.plans || plansRes?.data?.data || [];
    return {
      ...product,
      plans: plans.map((p) => ({
        ...p,
        price: p.price ?? p.price_cents ?? 0,
        status: p.status ?? (p.is_active ? 'active' : 'inactive'),
      })),
    };
  } catch (err) {
    console.warn(`Erro ao carregar planos do produto ${product.id}:`, err.message);
    return { ...product, plans: [] };
  }
}

const ProductsServicesStable = {
  /**
   * Lista produtos do owner autenticado.
   * Backend V2: GET /api/v2/products/me
   * Retorno V1-shape: array de produtos com `image` e `plans[].price`.
   */
  getProducts: async () => {
    try {
      const response = await productsV2API.listMine();
      const list = response.data?.products || response.data?.data || [];
      const productsV1Shape = mapProductListV2ToV1(list);
      const withPlans = await Promise.all(productsV1Shape.map(attachPlansV1Shape));
      return withPlans;
    } catch (error) {
      console.error('❌ Error in ProductsServicesStable.getProducts:', error);
      throw error;
    }
  },

  /**
   * Busca produto por ID numérico OU UUID.
   * Backend V2: GET /api/v2/products/:id  (regex \d+)
   *             GET /api/v2/products/uuid/:uuid
   *
   * O frontend legado às vezes navega com UUID (product_code) na URL,
   * então detectamos formato antes de chamar o endpoint certo.
   */
  getProductById: async (idOrUuid) => {
    try {
      const isUuid = typeof idOrUuid === 'string' && idOrUuid.includes('-');
      const response = isUuid
        ? await productsV2API.findByUuid(idOrUuid)
        : await productsV2API.findById(idOrUuid);
      const product = response.data?.product || response.data?.data || response.data;
      const v1Shape = mapProductV2ToV1(product);
      return await attachPlansV1Shape(v1Shape);
    } catch (error) {
      console.error('❌ Error in ProductsServicesStable.getProductById:', error);
      throw error;
    }
  },

  /**
   * Cria novo produto.
   * Backend V2: POST /api/v2/products
   */
  registerProduct: async (payload) => {
    try {
      const v2Payload = {
        name: payload.name,
        description: payload.description,
        category: payload.category,
        image_url: payload.image_url ?? payload.image,
        commission_type: payload.commission_type,
        commission_value_affiliate: payload.commission_value_affiliate,
        commission_value_manager: payload.commission_value_manager,
        commission_value_coproducer: payload.commission_value_coproducer,
      };
      const response = await productsV2API.create(v2Payload);
      const product = response.data?.product || response.data?.data || response.data;
      return { data: mapProductV2ToV1(product) };
    } catch (error) {
      console.error('Error in ProductsServicesStable.registerProduct:', error);
      throw error;
    }
  },

  /**
   * Atualiza produto.
   * Backend V2: PATCH /api/v2/products/:id
   */
  updateProduct: async (id, payload) => {
    try {
      const v2Payload = { ...payload };
      if ('image' in payload && !('image_url' in payload)) {
        v2Payload.image_url = payload.image;
        delete v2Payload.image;
      }
      const response = await productsV2API.update(id, v2Payload);
      const product = response.data?.product || response.data?.data || response.data;
      return { data: mapProductV2ToV1(product) };
    } catch (error) {
      console.error('Error in ProductsServicesStable.updateProduct:', error);
      throw error;
    }
  },

  /**
   * Soft-delete.
   * Backend V2: DELETE /api/v2/products/:id
   */
  deleteProduct: async (id) => {
    try {
      const response = await productsV2API.remove(id);
      return response.data;
    } catch (error) {
      console.error('Error in ProductsServicesStable.deleteProduct:', error);
      throw error;
    }
  },
};

export default ProductsServicesStable;
